/**
 * Shared Tasks-tab table controls — property search + independent pagination.
 *
 * These are PURE display helpers. They never change tab/card counts (which are
 * derived upstream from the full bucketed dataset); they only narrow and page
 * the rows actually rendered in a table body.
 *
 *   useTaskTable(rows, { keys, pageSize, resetKey })
 *     → { query, setQuery, page, setPage, totalPages, pageRows, total, pageSize }
 *
 *   <TaskSearchBar query setQuery />   — filters the currently visible table
 *   <TablePagination .../>             — Prev / Page x / y / Next (hidden if 1 page)
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Search, ChevronLeft, ChevronRight } from 'lucide-react';

export const matchRow = (row, keys, q) =>
  keys.some((k) => String(row?.[k] ?? '').toLowerCase().includes(q));

export const TaskSearchBar = ({
  query,
  setQuery,
  placeholder = 'Search by hotel / property name…',
  testId = 'task-search',
}) => (
  <div className="relative w-full sm:max-w-xs" data-testid={`${testId}-wrap`}>
    <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
    <input
      type="text"
      value={query}
      onChange={(e) => setQuery(e.target.value)}
      placeholder={placeholder}
      data-testid={testId}
      className="w-full pl-9 pr-8 py-2 text-sm border border-gray-200 rounded-lg bg-white focus:ring-2 focus:ring-teal-200 outline-none"
    />
    {query && (
      <button
        type="button"
        onClick={() => setQuery('')}
        data-testid={`${testId}-clear`}
        className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 text-xs"
        aria-label="Clear search"
      >
        ✕
      </button>
    )}
  </div>
);

export const TablePagination = ({ page, totalPages, setPage, total, pageSize, testId = 'task-pagination' }) => {
  if (totalPages <= 1) return null;
  const from = (page - 1) * pageSize + 1;
  const to = Math.min(page * pageSize, total);
  return (
    <div className="flex items-center justify-between px-5 py-3 border-t border-gray-100 text-xs text-gray-500" data-testid={testId}>
      <span data-testid={`${testId}-info`}>{from}–{to} of {total}</span>
      <div className="flex items-center gap-1">
        <button
          onClick={() => setPage(Math.max(1, page - 1))}
          disabled={page <= 1}
          data-testid={`${testId}-prev`}
          className="inline-flex items-center gap-1 px-2 py-1 rounded-md border border-gray-200 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
        >
          <ChevronLeft size={13} /> Prev
        </button>
        <span className="px-2" data-testid={`${testId}-page`}>Page {page} / {totalPages}</span>
        <button
          onClick={() => setPage(Math.min(totalPages, page + 1))}
          disabled={page >= totalPages}
          data-testid={`${testId}-next`}
          className="inline-flex items-center gap-1 px-2 py-1 rounded-md border border-gray-200 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
        >
          Next <ChevronRight size={13} />
        </button>
      </div>
    </div>
  );
};

export const useTaskTable = (rows, { keys = ['hotel_name'], pageSize = 10, resetKey = '' } = {}) => {
  const [query, setQuery] = useState('');
  const [page, setPage] = useState(1);
  const q = query.trim().toLowerCase();
  const filtered = useMemo(
    () => (q ? (rows || []).filter((r) => matchRow(r, keys, q)) : (rows || [])),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [rows, q],
  );
  // Reset to the first page whenever the search term OR the upstream
  // tab/scope (resetKey) changes — keeps pagination independent + predictable.
  useEffect(() => { setPage(1); }, [q, resetKey]);
  const total = filtered.length;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const safePage = Math.min(page, totalPages);
  const pageRows = filtered.slice((safePage - 1) * pageSize, safePage * pageSize);
  return { query, setQuery, page: safePage, setPage, totalPages, pageRows, total, pageSize };
};
